import { Alert, Button, Modal, TextField } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';
import { useCreateItemFromListing } from '../hooks/use-create-item-from-listing';
import { useListingMapping } from '../hooks/use-listing-mapping';
import { createItemBody } from '../lib/create-item';
import { mapBody } from '../lib/mappable-params';
import { mappingErrorMessage, takenItemId } from '../lib/mapping-errors';
import type { ListingRef } from '../lib/types';

export type CreateItemModalProps = {
  /** Shows the dialog while set. */
  listing: ListingRef | null;
  onClose: () => void;
};

/** The API refuses a title over 255 UTF-16 units, which is also what an input's maxLength counts. */
const TITLE_MAX = 255;

/** Cuts to TITLE_MAX without leaving half a surrogate pair at the end. */
function cutTitle(title: string) {
  const cut = title.slice(0, TITLE_MAX);
  const last = cut.charCodeAt(cut.length - 1);

  return title.length > TITLE_MAX && last >= 0xd800 && last <= 0xdbff
    ? cut.slice(0, -1)
    : cut;
}

type Failure = { message: string; takenItemId?: string };

/** Creates a catalog item from an unmapped listing, with its SKU and title to edit first. */
export function CreateItemModal({ listing, onClose }: CreateItemModalProps) {
  const create = useCreateItemFromListing();
  const mapping = useListingMapping();
  const [sku, setSku] = useState('');
  const [title, setTitle] = useState('');
  const [failure, setFailure] = useState<Failure | null>(null);

  /* Every listing opens with its own SKU and title. */
  const [openedFor, setOpenedFor] = useState(listing);
  if (openedFor !== listing) {
    setOpenedFor(listing);
    setSku(listing?.sku ?? '');
    setTitle(cutTitle(listing?.title ?? ''));
    setFailure(null);
  }

  const isProcessing = create.isPending || mapping.isPending;
  const trimmedSku = sku.trim();
  const listingTitle = listing?.title ?? '';
  const isTitleShortened =
    listingTitle.length > TITLE_MAX && title === cutTitle(listingTitle);

  const submit = async () => {
    if (!listing || !trimmedSku) return;
    setFailure(null);
    try {
      await create.mutateAsync({
        listingId: listing.id,
        body: createItemBody(sku, title),
      });
      ToastMessage.success('Item created and listing mapped');
      onClose();
    } catch (error) {
      setFailure({
        message: mappingErrorMessage(error, trimmedSku),
        takenItemId: takenItemId(error),
      });
    }
  };

  const mapToTaken = async (itemId: string) => {
    if (!listing) return;
    setFailure(null);
    try {
      await mapping.mutateAsync({
        listingId: listing.id,
        body: mapBody(listing, itemId),
      });
      ToastMessage.success('Listing mapped successfully');
      onClose();
    } catch (error) {
      setFailure({ message: mappingErrorMessage(error) });
    }
  };

  return (
    <Modal
      isOpen={!!listing}
      onOpenChange={isOpen => !isOpen && onClose()}
      title="Create item"
      isDismissable={!isProcessing}
      footer={
        <div className="sk:flex sk:justify-end sk:gap-2">
          <Button
            variant="pill"
            size="md"
            isDisabled={isProcessing}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            isDisabled={isProcessing || !trimmedSku}
            onPress={submit}
          >
            Create item
          </Button>
        </div>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-3">
        <p className="sk:m-0 sk:text-sm sk:text-fg-secondary">
          Creates a catalog item from this listing and maps the listing to it.
        </p>
        <TextField label="SKU" value={sku} onChange={setSku} isRequired />
        <TextField
          label="Title"
          value={title}
          onChange={setTitle}
          maxLength={TITLE_MAX}
        />
        {isTitleShortened ? (
          <p className="sk:m-0 sk:text-sm sk:text-fg-secondary">
            The listing title was shortened to {TITLE_MAX} characters.
          </p>
        ) : null}
        {failure ? (
          <Alert tone="danger">
            <div className="sk:flex sk:flex-col sk:items-start sk:gap-2">
              {failure.message}
              {failure.takenItemId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  isDisabled={isProcessing}
                  onPress={() => mapToTaken(failure.takenItemId as string)}
                >
                  Map to that item
                </Button>
              ) : null}
            </div>
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
}
